import { RefreshCw } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import type { ScanStatus } from "@/api/collectionStaging";

export interface ScanRecoveryControlsProps {
  scanStatus: ScanStatus;
  /** Concise, server-provided reason a scan errored / timed out. */
  scanError?: string | null;
  /** Whether the signed-in reviewer holds the scan-override/refresh permission. */
  canRefresh: boolean;
  onRefresh: () => void;
  refreshing?: boolean;
}

/**
 * Staging recovery — shared Refresh-scan control + error reason for both the
 * Deal Staging review drawer and the External Collection review-submission
 * drawer. Refresh re-triggers a stuck/errored scan (resets it to Pending);
 * it is intentionally NOT offered for Clean (nothing to do) or Infected (a
 * genuine result that must stay governing — use a separate unquarantine flow).
 * Distinct from the existing Scan override, which proceeds without a clean
 * result; this one tries the scan again.
 */
export function ScanRecoveryControls({
  scanStatus,
  scanError,
  canRefresh,
  onRefresh,
  refreshing = false,
}: ScanRecoveryControlsProps) {
  const refreshable = scanStatus === "Pending" || scanStatus === "Error";
  if (!refreshable && !scanError) return null;

  return (
    <div className="flex flex-wrap items-center gap-space-2" data-testid="scan-recovery-controls">
      {scanStatus === "Error" && scanError && (
        <span className="text-caption text-status-error">{scanError}</span>
      )}
      {refreshable && canRefresh && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={refreshing}
          onClick={onRefresh}
        >
          <RefreshCw size={14} className="mr-1 inline" /> Refresh scan
        </Button>
      )}
    </div>
  );
}
